import React, { useState } from 'react';
import './CreateUserModal.css';

/**
 * Create User Modal Component
 * Allows creation of new users with group assignment
 */
const CreateUserModal = ({ show, onHide, onSubmit, groups, loading }) => {
  const [formData, setFormData] = useState({
    uid: '',
    firstName: '',
    lastName: '',
    email: '',
    groupName: ''
  });
  const [errors, setErrors] = useState({});

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.uid.trim()) {
      newErrors.uid = 'User ID is required';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(formData.uid)) {
      newErrors.uid = 'User ID can only contain letters, numbers, dots, underscores, and hyphens';
    }
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.groupName) {
      newErrors.groupName = 'Group selection is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Note: Backend doesn't use passwords, so we'll use a placeholder
    const userData = {
      ...formData,
      password: 'temp_password_123' // Placeholder as per backend requirement
    };
    
    await onSubmit(userData);
    
    // Reset form on success
    setFormData({
      uid: '',
      firstName: '',
      lastName: '',
      email: '',
      groupName: ''
    });
    setErrors({});
  };

  // Reset form when modal is closed
  const handleClose = () => {
    setFormData({
      uid: '',
      firstName: '',
      lastName: '',
      email: '',
      groupName: ''
    });
    setErrors({});
    onHide();
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Create New User</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={loading}
            >
              <span>&times;</span>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="uid" className="form-label">
                  User ID <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="uid"
                  name="uid"
                  className={`form-control ${errors.uid ? 'is-invalid' : ''}`}
                  value={formData.uid}
                  onChange={handleChange}
                  placeholder="e.g., jdoe"
                  disabled={loading}
                />
                {errors.uid && <div className="invalid-feedback">{errors.uid}</div>}
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="firstName" className="form-label">
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      disabled={loading}
                    />
                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="lastName" className="form-label">
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      disabled={loading}
                    />
                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john.doe@example.com"
                  disabled={loading}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="groupName" className="form-label">
                  Assign to Group <span className="required">*</span>
                </label>
                <select
                  id="groupName"
                  name="groupName"
                  className={`form-select ${errors.groupName ? 'is-invalid' : ''}`}
                  value={formData.groupName}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select a group...</option>
                  {groups.map((group, index) => (
                    <option key={index} value={group.name}>
                      {group.name} {group.description && `(${group.description})`}
                    </option>
                  ))}
                </select>
                {errors.groupName && <div className="invalid-feedback">{errors.groupName}</div>}
              </div>

              <div className="form-note">
                <p className="text-muted">
                  <i className="fas fa-info-circle"></i>
                  Note: Users in this system don't use passwords. Authentication is handled via OTP.
                </p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus me-2"></i>
                    Create User
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
